import type { QualificationResult, WizardAnswers, Lang } from "./types";

interface PdfStrings {
  title: string;
  prepared_for: string;
  service: string;
  ticket: string;
  documents_to_prepare: string;
  prefecture_label: string;
  contact_label: string;
  footer: string;
  doc_labels: Record<string, string>;
}

const STRINGS: Record<Lang, PdfStrings> = {
  fr: {
    title: "LevelCare — Ta checklist personnalisée",
    prepared_for: "Préparée pour",
    service: "Service recommandé",
    ticket: "Ticket estimé",
    documents_to_prepare: "Pièces à préparer",
    prefecture_label: "Préfecture compétente",
    contact_label: "Contact LevelCare",
    footer: "LevelCare — 25 Rue des bas, 92600 Asnières-sur-Seine — contact@levelcare.fr",
    doc_labels: {
      passeport: "Passeport en cours de validité",
      visa_long_sejour: "Visa long séjour",
      certificat_scolarite: "Certificat de scolarité",
      attestation_logement: "Attestation de logement",
      ofii_attestation: "Attestation OFII",
      contrat_travail: "Contrat de travail",
      "3_bulletins_salaire": "3 derniers bulletins de salaire",
      attestation_employeur: "Attestation employeur",
      justif_domicile_3_mois: "Justificatif de domicile (- de 3 mois)",
      titre_actuel: "Titre de séjour actuel",
      "fiches_paie_6_mois_ou_avis_imposition": "Fiches de paie 6 mois OU avis d'imposition",
      photo_identite: "Photo d'identité aux normes",
      timbre_fiscal: "Timbre fiscal",
      titre_expire: "Titre de séjour expiré",
      preuve_continuite_residence: "Preuves de continuité de résidence",
      titre_de_sejour: "Titre de séjour",
      acte_naissance_traduit: "Acte de naissance traduit (assermenté)",
      casier_judiciaire_pays_origine: "Casier judiciaire du pays d'origine",
      casier_judiciaire_france: "Casier judiciaire français",
      "fiches_paie_3_ans_ou_bilans": "Fiches de paie 3 ans OU bilans",
      avis_imposition_3_ans: "Avis d'imposition 3 dernières années",
      justif_domicile: "Justificatif de domicile",
      attestation_b2_francais: "Attestation niveau B2 français (obligatoire 2026)",
      titre_vie_privee_familiale: "Titre vie privée et familiale",
      livret_famille_traduit: "Livret de famille traduit",
      acte_mariage_transcrit: "Acte de mariage transcrit",
      preuve_communaute_de_vie_4_ans: "Preuves de communauté de vie 4 ans",
      justif_domicile_avec_surface_logement: "Justificatif de domicile avec surface logement",
      "12_fiches_paie_ou_avis_imposition_12_mois": "12 fiches de paie OU avis d'imposition 12 mois",
      ofii_attestation_parcours_integration: "Attestation OFII parcours d'intégration",
      ancienne_cnie_ou_passeport: "Ancienne CNIE ou passeport marocain",
      photos_format_marocain: "Photos format consulat marocain",
      acte_naissance_marocain_recent: "Acte de naissance marocain récent",
      acte_a_transcrire_original: "Acte à transcrire (original)",
      traduction_assermentee: "Traduction assermentée",
      passeport_marocain: "Passeport marocain",
      livret_famille_si_applicable: "Livret de famille (si applicable)",
      decision_administrative_recente: "Décision administrative récente (refus, OQTF, etc.)",
      tout_courrier_prefecture: "Tout courrier de la préfecture",
      passeport_ou_acte_naissance: "Passeport OU acte de naissance",
      preuves_presence_france_3_ans: "Preuves de présence en France sur 3 ans",
      "fiches_paie_meme_dissimulees": "Fiches de paie (même travail dissimulé)",
      tout_justif_domicile: "Tout justificatif de domicile possible",
      tout_courrier_administratif_recent: "Tout courrier administratif récent",
      titre_actuel_si_existe: "Titre actuel (si existe)",
      piece_identite: "Pièce d'identité",
      courrier_concernant_demarche: "Courrier concernant la démarche",
    },
  },
  ar: {
    title: "LevelCare — قائمتك الشخصية",
    prepared_for: "أعدت لـ",
    service: "الخدمة الموصى بها",
    ticket: "السعر التقديري",
    documents_to_prepare: "الوثائق المطلوبة",
    prefecture_label: "العمالة المختصة",
    contact_label: "اتصل بـ LevelCare",
    footer: "LevelCare — 25 Rue des bas, 92600 Asnières-sur-Seine — contact@levelcare.fr",
    doc_labels: {},
  },
  darija: {
    title: "LevelCare — Lista dyalek",
    prepared_for: "M3awna l",
    service: "Lkhedma li ghadi t9oum biha",
    ticket: "Taman taqribi",
    documents_to_prepare: "Lwarq li khaskom tjib",
    prefecture_label: "Lprefecture li khasse",
    contact_label: "Tawassal m3a LevelCare",
    footer: "LevelCare — 25 Rue des bas, 92600 Asnières-sur-Seine — contact@levelcare.fr",
    doc_labels: {},
  },
};

export interface ChecklistInput {
  answers: WizardAnswers;
  result: QualificationResult;
  lang: Lang;
}

export async function generateChecklistPdf(input: ChecklistInput): Promise<Uint8Array> {
  // Lazy-load pdf-lib — only pulled in when the user actually downloads.
  const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");

  // pdf-lib StandardFonts ne supportent pas l'arabe — on force fr en repli pour AR/Darija pour la démo.
  const lang: Lang = input.lang === "ar" ? "fr" : input.lang;
  const s = STRINGS[lang] ?? STRINGS.fr;
  const docLabels = STRINGS.fr.doc_labels;

  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdf.embedFont(StandardFonts.HelveticaBold);

  const page = pdf.addPage([595, 842]); // A4 portrait, points
  const { width, height } = page.getSize();

  const ink = rgb(0.043, 0.122, 0.227); // #0B1F3A
  const muted = rgb(0.42, 0.47, 0.52); // #6B7785
  const signal = rgb(0.027, 0.596, 0.859); // #0798DB

  let y = height - 60;

  // Header band
  page.drawRectangle({ x: 0, y: height - 12, width, height: 12, color: signal });

  // Logo wordmark (text-only fallback)
  page.drawText("LevelCare", {
    x: 40,
    y,
    size: 22,
    font: fontBold,
    color: ink,
  });
  y -= 36;

  // Title
  page.drawText(s.title, { x: 40, y, size: 16, font: fontBold, color: ink });
  y -= 28;

  // Prepared for
  page.drawText(`${s.prepared_for}: ${input.answers.contact.prenom}`, {
    x: 40,
    y,
    size: 11,
    font,
    color: muted,
  });
  y -= 28;

  // Service block
  page.drawText(s.service, { x: 40, y, size: 10, font, color: muted });
  y -= 14;
  page.drawText(input.result.service, { x: 40, y, size: 13, font: fontBold, color: ink });
  y -= 22;

  // Ticket
  if (input.result.ticket_visible && input.result.ticket_estimate !== null && input.result.ticket_estimate > 0) {
    page.drawText(s.ticket, { x: 40, y, size: 10, font, color: muted });
    y -= 14;
    const ticketText = input.result.ticket_max
      ? `${input.result.ticket_estimate} - ${input.result.ticket_max} EUR`
      : `${input.result.ticket_estimate} EUR`;
    page.drawText(ticketText, { x: 40, y, size: 13, font: fontBold, color: ink });
    y -= 22;
  }

  // Préfecture
  if (input.result.prefecture) {
    page.drawText(s.prefecture_label, { x: 40, y, size: 10, font, color: muted });
    y -= 14;
    page.drawText(input.result.prefecture.name, { x: 40, y, size: 11, font, color: ink });
    y -= 14;
    if (input.result.prefecture.address) {
      page.drawText(input.result.prefecture.address, { x: 40, y, size: 10, font, color: muted });
      y -= 22;
    } else {
      y -= 8;
    }
  }

  // Documents block
  if (input.result.documents_required.length > 0) {
    page.drawLine({ start: { x: 40, y }, end: { x: width - 40, y }, thickness: 0.5, color: muted });
    y -= 24;

    page.drawText(s.documents_to_prepare, { x: 40, y, size: 14, font: fontBold, color: ink });
    y -= 20;

    for (const docId of input.result.documents_required) {
      const label = docLabels[docId] ?? docId;
      // Checkbox
      page.drawRectangle({
        x: 40,
        y: y - 3,
        width: 12,
        height: 12,
        borderColor: ink,
        borderWidth: 1,
      });
      page.drawText(label, { x: 60, y, size: 11, font, color: ink });
      y -= 22;

      if (y < 100) {
        // New page
        const newPage = pdf.addPage([595, 842]);
        newPage.drawRectangle({ x: 0, y: 842 - 12, width: 595, height: 12, color: signal });
        y = 842 - 60;
      }
    }
  }

  // Footer
  page.drawText(s.footer, { x: 40, y: 30, size: 9, font, color: muted });

  return pdf.save();
}

export function downloadPdf(bytes: Uint8Array, filename: string): void {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
